import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import api from "services/api";
import {
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  ListItemIcon,
  Typography,
  Stack,
  Button,
  ListItemText,
  Divider,
  Popover,
  Popper,
  Paper,
  Tooltip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
} from "@mui/material";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";
import ColumnFilter from "components/ColumnFilter";
import ColumnDateFilter from "components/ColumnDateFilter";
import DataGridHeader from "components/DataGridHeader";
import useColumnFilters from "hooks/useColumnFilters";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import {
  DataGrid,
  GridFooter,
  GridFooterContainer,
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
} from "@mui/x-data-grid";
import ExcelJS from "exceljs";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useNavigate } from "react-router-dom";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import Grid from "@mui/material/Grid2";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import AddTaskRoundedIcon from "@mui/icons-material/AddTaskRounded";
import TaskRoundedIcon from "@mui/icons-material/TaskRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import dayjs from "dayjs";
import CustomDateSelector from "components/CustomDateSelector";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import { CONST } from "constants/CONST";
import CEQuickFilter from "components/CEQuickFilter";
import { usePageTitle } from "hooks/usePageTitle";
import {
  getDeadlineProximityColor,
  getStatusColor,
  numericComparator,
} from "helpers/ServiceHelpers";
import CommentRoundedIcon from "@mui/icons-material/CommentRounded";
import { NAVBAR_HEIGHT } from "constants/layout";
import useAssignmentUpdates from "hooks/useAssignmentUpdates";
import { usePresence } from "contexts/PresenceContext";
import { usePagePresence } from "hooks/useAssignmentPresence";

const Translations = () => {
  usePageTitle("Translations");
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();
  const navigate = useNavigate();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const [isLoading, setIsLoading] = useState(true);

  // Grid options state
  const getInitialDensity = () => {
    const saved = localStorage.getItem("TRANSLATIONS_GRID_DENSITY");
    return saved || "standard";
  };
  const getInitialColumnsVisible = () => {
    const saved = localStorage.getItem("TRANSLATIONS_COLUMNS_VISIBLE");
    const parsed = saved ? JSON.parse(saved) : {};

    return {
      ...parsed,
      LinguistPay: true,
      TotalLinguistPay: true,
      AdditionalCost: true,
      AdditionalLinguistCost: true,
      EquipmentCost: true,
      DeliveryCost: true,
      PaymentFees: true,
      NotarizationOrCOTCost: true,
    };
  };
  const [density, setDensity] = useState(getInitialDensity());
  const [columnsVisible, setColumnsVisible] = useState(
    getInitialColumnsVisible(),
  );

  // Grid API ref for DataGrid exports
  const apiRef = useGridApiRef();

  // Overlay state for Density & Columns
  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayMode, setOverlayMode] = useState(null); // 'density' or 'columns'

  // Save density to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("TRANSLATIONS_GRID_DENSITY", density);
  }, [density]);

  // Save columns visibility to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(
      "TRANSLATIONS_COLUMNS_VISIBLE",
      JSON.stringify(columnsVisible),
    );
  }, [columnsVisible]);

  // Track last update for snackbar notification
  const [lastUpdate, setLastUpdate] = useState(null);

  // Set up entity tracking for Translations list to receive real-time updates
  const { setCurrentEntity } = usePresence();
  usePagePresence();

  useEffect(() => {
    // Set entity to TranslationsList so socket joins the entity room for list-level updates
    setCurrentEntity({
      entityType: "TranslationsList",
      entityId: "all",
    });

    return () => {
      // Clean up entity tracking when component unmounts
      setCurrentEntity({ entityType: null, entityId: null });
    };
  }, [setCurrentEntity]);

  const { setOverlayData, setOverlayName } =
    useStateContext();
  const { refreshPage, showSnackbar } = useStateContext();

  const [minMaxDates, setMinMaxDates] = useState();

  // Month selector data (static order)
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Get saved months from localStorage or use current month as default
  const getSavedMonths = () => {
    const savedMonths = localStorage.getItem(CONST.TRANSLATION_MONTHS_KEY);
    return savedMonths ? JSON.parse(savedMonths) : [dayjs().format("MMM")];
  };

  const [selectedMonths, setSelectedMonths] = useState(getSavedMonths);
  const [tempSelectedMonths, setTempSelectedMonths] = useState(getSavedMonths);

  // Year selector data - dynamically generated from minMaxDates
  const currentYear = dayjs().year();

  const getSavedYear = () => {
    const savedYear = localStorage.getItem(CONST.TRANSLATION_YEAR_KEY);
    return savedYear ? JSON.parse(savedYear) : [String(currentYear)];
  };

  const [yearOptions, setYearOptions] = useState([String(currentYear)]);

  const [selectedYear, setSelectedYear] = useState(getSavedYear);
  const [tempSelectedYear, setTempSelectedYear] = useState(getSavedYear);

  const [, setErrorMessage] = useState("");
  const { openDialog, handleErrorDialog, closeDialog } = useStateContext();

  const [listOfClients, setListOfClients] = useState([]);
  const [listOfLinguists, setListOfLinguists] = useState([]);
  const [listOfLanguages, setListOfLanguages] = useState([]);
  const [listOfStatuses, setListOfStatuses] = useState([]);
  const [listOfTypes, setListOfTypes] = useState([]);
  const [latestProjectNumber, setLatestProjectNumber] = useState(0);
  const [deliveryTypes, setDeliveryTypes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [rateTypes, setRateTypes] = useState(0);
  const [isDataListsReady, setIsDataListsReady] = useState(false);
  const [pendingCreateNew, setPendingCreateNew] = useState(false);
  const fetchTranslationsDataRef = useRef(() => {});
  const getDataListsRef = useRef(() => {});
  const hasInitializedDataRef = useRef(false);
  // Scroll position to restore after a refetch (saves bounce the list to top).
  // The DataGrid's virtual scroller is the ONLY scroller on this page (the
  // page itself never scrolls), so we save its pixel offset plus the first
  // visible ROW id — the restore anchors to that row via the grid API, which
  // re-syncs the virtualizer (a raw scrollTop write can leave the scrollbar
  // mid-track while the grid renders from index 0).
  const listScrollRestoreRef = useRef(0);
  const listFirstVisibleRowIdRef = useRef(null);

  const [contextMenu, setContextMenu] = useState(null);
  const [listOfServices, setListOfServices] = useState(null);

  const [selectedRow, setSelectedRow] = React.useState();

  // Status submenu state (context-menu "Status >" item)
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const statusMenuCloseTimerRef = useRef(null);

  // Derived selection for context menu actions
  const selectedTranslation = useMemo(() => {
    return (listOfServices || []).find(({ id }) => id === selectedRow);
  }, [listOfServices, selectedRow]);

  const canCompleteSelected =
    selectedTranslation &&
    selectedTranslation.TranslationStatus !== "Completed";

  // Statuses offered in the context-menu submenu (Cancelled variants excluded)
  const statusOptions = ["In-Progress", "Delivered", "Completed"];
  const currentStatus = selectedTranslation?.TranslationStatus;

  // Selection controls
  const [selectionEnabled, setSelectionEnabled] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const longPressTimerRef = useRef(null);
  const suppressNextClickRef = useRef(false);
  const LONG_PRESS_MS = 500;

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Client" -> "TRANSLATIONS_FILTER_CLIENT"
    const upperFieldName = fieldName.toUpperCase();
    return `TRANSLATIONS_FILTER_${upperFieldName}`;
  }, []);

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback(
    (fieldName, values) => {
      const key = getFilterStorageKey(fieldName);
      if (values && values.length > 0) {
        localStorage.setItem(key, JSON.stringify(values));
      } else {
        // Remove from localStorage if no values selected
        localStorage.removeItem(key);
      }
    },
    [getFilterStorageKey],
  );

  // Helper function to load a column filter from localStorage
  const loadColumnFilter = useCallback(
    (fieldName) => {
      const key = getFilterStorageKey(fieldName);
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : [];
    },
    [getFilterStorageKey],
  );

  // Helper function to load all saved column filters
  const loadAllColumnFilters = useCallback(() => {
    const savedFilters = {};
    // Get all column field names
    const fields = [
      "ProjectNumber",
      "Date",
      "Deadline",
      "Client",
      "Requestor",
      "TranslationStatus",
      "AccountCharge",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Linguist",
      "Language",
      "TargetLanguage",
      "Profit",
      "Margin",
      "TranslationType",
      "Rate",
      "Cancelled",
      "CancellationReason",
      "Invoiced",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fields.forEach((field) => {
      const values = loadColumnFilter(field);
      if (values.length > 0) {
        savedFilters[field] = values;
      }
    });

    return savedFilters;
  }, [loadColumnFilter]);

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ProjectNumber",
      "Date",
      "Deadline",
      "Client",
      "Requestor",
      "TranslationStatus",
      "AccountCharge",
      "TotalCost",
      "AdditionalCost",
      "LinguistPay",
      "Linguist",
      "Language",
      "TargetLanguage",
      "Profit",
      "Margin",
      "TranslationType",
      "Rate",
      "Cancelled",
      "CancellationReason",
      "Invoiced",
      "CreatedBy",
      "CheckedBy",
      "CheckedOn",
    ];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const handleContextMenu = (event) => {
    event.preventDefault();

    const rowId = Number(event.currentTarget.getAttribute("data-id"));
    setSelectedRow(rowId);

    if (Number.isFinite(rowId)) {
      setSelectionModel((prev) => {
        if (selectionEnabled) {
          return prev.includes(rowId) ? prev : [...prev, rowId];
        }
        return [rowId];
      });
    }

    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
          // Other native context menus might behave different.
          // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
          null,
    );
  };

  const handleContextMenuEdit = () => {
    api
      .get(`/services/translations/${selectedRow}`)
      .then((response) => {
        const translationData = response.data;

        const secondData = {
          ...translationData,
          Action: OVERLAY_NAMES.TRANSLATION_ACTION_EDIT,
          Translations: listOfServices,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          TranslationStatuses: listOfStatuses,
          TranslationTypes: listOfTypes,
          RateTypes: rateTypes,
        };

        setOverlayData(secondData);
        setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuAddTask = () => {
    const translationData = listOfServices.find(({ id }) => id === selectedRow);

    setOverlayData({
      Action: OVERLAY_NAMES.TASK_ACTION_NEW,
      Task: {
        EntityType: "Service",
        EntityID: selectedRow,
        ServiceType: "Translation",
      },
      Service: {
        id: translationData.id,
        ProjectNumber: translationData.ProjectNumber,
        Details: translationData.Details,
        ServiceCategory: translationData.ServiceCategory,
      },
    });
    setOverlayName(OVERLAY_NAMES.TASK_NEW_EDIT);
    setContextMenu(null);
  };

  const handleContextMenuAddNote = () => {
    const data = {
      ServiceID: selectedRow,
      MemberID: currentUser.id,
      ModelType: "Translation",
      Action: OVERLAY_NAMES.SERVICE_NOTE_ACTION_NEW,
    };

    setOverlayData(data);

    setOverlayName(OVERLAY_NAMES.SERVICE_NOTE_NEW_EDIT);

    setContextMenu(null);
  };

  const extractCostLinesFromService = (costLines) => {
    if (!Array.isArray(costLines) || costLines.length === 0) {
      return [];
    }

    return costLines.map((line) => ({
      CostTypeName: line.CostType?.Name || line.CostTypeName || "",
      SourceLegacyField: line.SourceLegacyField || null,
      Amount: Number(line.Amount || 0).toFixed(2),
      IncludeInTotalCost: line.IncludeInTotalCost ?? true,
      IncludeInAccountCharge: line.IncludeInAccountCharge ?? false,
      DisplayOrder: line.DisplayOrder || 0,
    }));
  };

  const handleContextMenuDuplicateConfirm = () => {
    api
      .get(`/services/translations/${selectedRow}`)
      .then(async (response) => {
        const translationData = response.data;

        if (!translationData) return;

        let fetchedCostLines = [];
        try {
          const costLinesResponse = await api.get(
            `/serviceCosts/service/${selectedRow}/lines`,
          );
          fetchedCostLines = Array.isArray(costLinesResponse.data?.lines)
            ? costLinesResponse.data.lines
            : [];
        } catch (error) {
          console.error("Error loading translation cost lines for duplicate:", error);
        }

        const duplicateCostLines = extractCostLinesFromService(
          fetchedCostLines.length > 0
            ? fetchedCostLines
            : Array.isArray(translationData.CostLines)
              ? translationData.CostLines
              : [],
        );

        // Extract target languages
        const targetLanguages =
          translationData.ServiceTargetLanguages &&
          translationData.ServiceTargetLanguages.length > 0
            ? translationData.ServiceTargetLanguages.map((tl) => tl.Language)
            : [];

        const normalizedUnitTypeId = translationData.UnitTypeID || null;
        const normalizedUnitCount =
          translationData.UnitCount ?? translationData.WordCount ?? null;
        const legacyWordCount =
          translationData.UnitType?.Name === "Word"
            ? normalizedUnitCount
            : (translationData.WordCount ?? null);

        const duplicatedData = {
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          Translations: listOfServices,
          TranslationStatuses: listOfStatuses,
          TranslationTypes: listOfTypes,
          RateTypes: rateTypes,
          ProjectNumber: latestProjectNumber,
          Action: OVERLAY_NAMES.TRANSLATION_ACTION_NEW,
          PrefilledData: {
            Date: translationData.Date,
            Deadline: translationData.Deadline || null,
            DeliveryDate: translationData.DeliveryDate || null,
            Name: translationData.Name,
            Details: translationData.Details,
            TranslationStatus: translationData.TranslationStatus,
            AccountCharge: translationData.AccountCharge,
            TotalCost: translationData.TotalCost,
            TotalLinguistPay: translationData.TotalLinguistPay,
            AdditionalCost: translationData.AdditionalCost,
            AdditionalLinguistCost: translationData.AdditionalLinguistCost,
            LinguistPay: translationData.LinguistPay,
            NotarizationOrCOTCost: translationData.NotarizationOrCOTCost,
            DeliveryCost: translationData.DeliveryCost,
            DeliveryType: translationData.DeliveryType,
            PaymentFees: translationData.PaymentFees,
            PaymentType: translationData.PaymentType,
            Profit: translationData.Profit,
            Margin: translationData.Margin,
            TranslationType: translationData.ServiceSubtype?.Name || null,
            RateType: translationData.RateType,
            Rate: translationData.Rate,
            UnitTypeID: normalizedUnitTypeId,
            UnitCount: normalizedUnitCount,
            WordCount: legacyWordCount,
            NumberOfLanguages: targetLanguages.length,
            SourceLanguage: translationData.Language,
            TargetLanguages: targetLanguages,
            Client: translationData.Client,
            Requestor: translationData.Requestor,
            Linguist: translationData.Linguist,
            Invoicer: translationData.Invoicer,
            CostLines: duplicateCostLines,
          },
        };

        setOverlayData(duplicatedData);
        setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
        setContextMenu(null);
        closeDialog();
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuDuplicate = () => {
    openDialog({
      theme: theme,
      title: "Duplicate Assignment",
      message:
        "Are you sure you want to duplicate this translation assignment?",
      secondaryAction: "Close",
      primaryAction: "Duplicate",
      secondaryOnClick: () => {
        closeDialog();
        setContextMenu(null);
      },
      primaryOnClick: () => {
        handleContextMenuDuplicateConfirm();
      },
    });
  };

  const handleContextMenuComplete = () => {
    api
      .get(`/services/translations/${selectedRow}`)
      .then((response) => {
        const translationData = response.data;

        const data = {
          ...translationData,
          Action: OVERLAY_NAMES.TRANSLATION_ACTION_COMPLETE,
          Clients: listOfClients,
          Linguists: listOfLinguists,
          Languages: listOfLanguages,
          TranslationStatuses: listOfStatuses,
          TranslationTypes: listOfTypes,
          ProjectNumber: latestProjectNumber,
          RateTypes: rateTypes,
          DeliveryTypes: deliveryTypes,
          PaymentTypes: paymentTypes,
        };

        setOverlayData(data);
        setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
        setContextMenu(null);
      })
      .catch((error) => {
        const message =
          error.response?.data?.message || "Failed to complete service.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const handleContextMenuCallRequestor = () => {
    const serviceData = listOfServices.find(({ id }) => id === selectedRow);

    const requestorPhones = serviceData?.Requestor?.PhoneNumbers || [];
    const requestorPrimary = requestorPhones.find((p) => p.IsPrimary) || requestorPhones[0];
    const requestorPhone = requestorPrimary?.Number || serviceData?.Requestor?.PhoneNumber;
    if (requestorPhone) {
      window.location.href = `tel:${requestorPhone.replace(/[^0-9+]/g, "")}`;
    }
    setContextMenu(null);
  };

  const handleContextMenuEmailRequestor = () => {
    const serviceData = listOfServices.find(({ id }) => id === selectedRow);

    if (serviceData?.Requestor?.Email) {
      const email = serviceData.Requestor.Email;
      const subject = "Regarding Assignment N° " + serviceData.ProjectNumber;
      const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(
        subject,
      )}`;
      window.open(mailtoLink, "_blank");
    }
    setContextMenu(null);
  };

  const handleContextMenuClose = () => {
    setContextMenu(null);
    setStatusMenuAnchor(null);
  };

  // Status submenu handlers
  const handleStatusMenuOpen = (event) => {
    if (statusMenuCloseTimerRef.current) {
      clearTimeout(statusMenuCloseTimerRef.current);
      statusMenuCloseTimerRef.current = null;
    }
    setStatusMenuAnchor(event.currentTarget);
  };

  const handleStatusMenuEnter = () => {
    if (statusMenuCloseTimerRef.current) {
      clearTimeout(statusMenuCloseTimerRef.current);
      statusMenuCloseTimerRef.current = null;
    }
  };

  const handleStatusMenuClose = () => {
    if (statusMenuCloseTimerRef.current) {
      clearTimeout(statusMenuCloseTimerRef.current);
      statusMenuCloseTimerRef.current = null;
    }
    setStatusMenuAnchor(null);
  };

  const handleStatusMenuLeave = () => {
    statusMenuCloseTimerRef.current = setTimeout(() => {
      setStatusMenuAnchor(null);
    }, 150);
  };

  const handleContextMenuStatusChange = (status) => {
    setStatusMenuAnchor(null);
    setContextMenu(null);

    if (status === currentStatus) return;

    api
      .post("/services/updateStatus", {
        serviceId: selectedRow,
        status,
      })
      .then((response) => {
        const updatedService = response.data;
        showSnackbar(
          `Status updated to "${status}" for ${updatedService.ProjectNumber || "service"}.`,
          theme,
          "success",
        );

        // Refresh the list (existing UI invalidation behavior)
        fetchTranslationsData(selectedMonths, selectedYear);

        // Emit assignment saved events for other viewers (same as the edit flow)
        if (socket) {
          const userPayload = {
            id: currentUser.id,
            name: currentUser.Name,
            imageThumbnail: currentUser.ImageThumbnail,
          };
          socket.emit("assignment:saved", {
            modelType: "Translation",
            recordId: updatedService.id,
            user: userPayload,
            projectNumber: updatedService.ProjectNumber,
          });
          socket.emit("assignment:saved", {
            modelType: "TranslationsList",
            recordId: "all",
            user: userPayload,
            projectNumber: updatedService.ProjectNumber,
          });
        }
      })
      .catch((error) => {
        const message =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to update service status.";
        handleErrorDialog(message, theme);
        showSnackbar(message, theme, "error");
      });
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowMouseDown = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowMouseUp = () => {
    cancelLongPress();
  };

  const handleRowTouchStart = (event) => {
    const idAttr = event.currentTarget.getAttribute("data-id");
    const id = idAttr ? Number(idAttr) : null;
    cancelLongPress();
    longPressTimerRef.current = setTimeout(() => {
      setSelectionEnabled(true);
      suppressNextClickRef.current = true;
      if (id != null) {
        setSelectionModel((prev) => (prev.includes(id) ? prev : [...prev, id]));
      }
    }, LONG_PRESS_MS);
  };

  const handleRowTouchEnd = () => {
    cancelLongPress();
  };

  const handleRowSelectionModelChange = (newSelection) => {
    if (selectionEnabled) {
      setSelectionModel(newSelection);
    } else {
      // keep last selected for highlighting
      setSelectionModel(newSelection.slice(-1));
    }
  };

  const handleCancelSelection = () => {
    setSelectionModel([]);
    setSelectionEnabled(false);
    suppressNextClickRef.current = false;
  };

  const isServiceChecked = useCallback((service) => {
    return !!(service?.CheckingDate || service?.CheckedBy || service?.Checker);
  }, []);

  const selectionStats = useMemo(() => {
    if (!selectionEnabled || selectionModel.length === 0) {
      return { total: 0, checked: 0, unchecked: 0, allChecked: false };
    }

    const selectedSet = new Set(selectionModel);
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedSet.has(service.id),
    );

    const checked = selectedServices.filter(isServiceChecked).length;
    const total = selectedServices.length;
    const unchecked = Math.max(total - checked, 0);

    return {
      total,
      checked,
      unchecked,
      allChecked: total > 0 && unchecked === 0,
    };
  }, [selectionEnabled, selectionModel, listOfServices, isServiceChecked]);

  const handleMarkSelectedAsChecked = () => {
    if (selectionModel.length === 0) return;

    const selectedIds = [...selectionModel];
    const selectedServices = (listOfServices || []).filter((service) =>
      selectedIds.includes(service.id),
    );
    const uncheckedIds = selectedServices
      .filter((service) => !isServiceChecked(service))
      .map((service) => service.id);

    if (uncheckedIds.length === 0) {
      showSnackbar(
        "The selected assignment(s) are already checked.",
        theme,
        "info",
      );
      return;
    }

    const message =
      uncheckedIds.length === selectedIds.length
        ? `Mark ${selectedIds.length} assignment(s) as Checked?`
        : `Only ${uncheckedIds.length} out of ${selectedIds.length} of the selected assignments are not checked, do you want to mark them as checked?`;

    openDialog({
      theme,
      title: "Mark Assignments as Checked",
      message,
      secondaryAction: "Cancel",
      primaryAction: "Confirm",
      secondaryOnClick: () => {
        closeDialog();
      },
      primaryOnClick: async () => {
        try {
          const payloads = uncheckedIds.map((id) => {
            const data = {
              id,
              CheckedBy: currentUser.id,
              CheckingDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
            };
            return api.post("/services/markServiceChecked", {
              data,
            });
          });

          await Promise.all(payloads);
          showSnackbar("Assignments marked as Checked", theme, "success");
          handleCancelSelection();
          fetchTranslationsData(selectedMonths, selectedYear);
        } catch (error) {
          const message =
            error.response?.data?.message ||
            "Failed to mark Translations as checked.";
          handleErrorDialog(message, theme);
          showSnackbar(message, theme, "error");
        } finally {
          closeDialog();
        }
      },
    });
  };

  // Generate dynamic year options when minMaxDates is fetched
  useEffect(() => {
    if (minMaxDates && minMaxDates.startDate && minMaxDates.endDate) {
      const startYear = minMaxDates.startDate.year;
      const endYear = minMaxDates.endDate.year;
      const years = [];
      for (let year = startYear; year <= endYear; year++) {
        years.push(String(year));
      }
      setYearOptions(years);

      // Validate current selected year
      if (selectedYear.length > 0) {
        const currentSelectedYear = parseInt(selectedYear[0]);
        if (currentSelectedYear < startYear || currentSelectedYear > endYear) {
          let newYear;
          if (currentYear >= startYear && currentYear <= endYear) {
            newYear = String(currentYear);
          } else if (currentYear < startYear) {
            newYear = String(startYear);
          } else {
            newYear = String(endYear);
          }
          setSelectedYear([newYear]);
          setTempSelectedYear([newYear]);
        }
      }
    }
  }, [minMaxDates, currentYear, selectedYear]); // Add currentYear to dependencies

  // Handle date selection save (combined months and years)
  const handleDateSelectionSave = (newSelection) => {
    // Ensure only one year is selected
    const updatedSelection = {
      months: newSelection.months,
      years: newSelection.years.slice(-1), // Take only the last selected year
    };

    // Update state
    setSelectedMonths(updatedSelection.months);
    setTempSelectedMonths(updatedSelection.months);
    setSelectedYear(updatedSelection.years);
    setTempSelectedYear(updatedSelection.years);

    // Save to localStorage
    localStorage.setItem(
      CONST.TRANSLATION_MONTHS_KEY,
      JSON.stringify(updatedSelection.months),
    );
    localStorage.setItem(
      CONST.TRANSLATION_YEAR_KEY,
      JSON.stringify(updatedSelection.years),
    );

    // Fetch data
    fetchTranslationsData(updatedSelection.months, updatedSelection.years);
  };

  // Fetch translations data - memoized with useCallback to prevent listener churn
  const fetchTranslationsData = useCallback(
    (months = selectedMonths, year = selectedYear) => {
      setIsLoading(true);

      // Capture the current scroll position so a refetch (e.g. after saving
      // an edit) doesn't bounce the list back to the top. Save the virtual
      // scroller's pixel offset AND the first VISIBLE row id (anchoring to a
      // row is the reliable way to resync the virtualizer on restore).
      // Queries are scoped to THIS grid's root element via its own apiRef —
      // a global document.querySelector can match a DIFFERENT grid mounted
      // elsewhere on the page (that is why the restore worked on one list
      // but not another).
      const captureRoot = apiRef.current?.rootElementRef?.current || document;
      const scroller = captureRoot.querySelector(
        ".MuiDataGrid-virtualScroller",
      );
      if (scroller && scroller.scrollTop > 0) {
        listScrollRestoreRef.current = scroller.scrollTop;
      }
      // Only anchor by row when the user was actually scrolled down — at the
      // top we must stay at the top (a new row inserted above would otherwise
      // yank the list). Pick the first row actually VISIBLE in the viewport
      // (the first DOM row may be an off-screen buffer row above it).
      let firstVisibleRowEl = null;
      if (scroller && scroller.scrollTop > 0) {
        const scRect = scroller.getBoundingClientRect();
        const rowEls = scroller.querySelectorAll(".MuiDataGrid-row[data-id]");
        for (const el of rowEls) {
          if (el.getBoundingClientRect().bottom > scRect.top + 1) {
            firstVisibleRowEl = el;
            break;
          }
        }
      }
      listFirstVisibleRowIdRef.current = firstVisibleRowEl
        ? firstVisibleRowEl.getAttribute("data-id")
        : null;
      // Ensure parameters are valid
      if (!months || months.length === 0) {
        months = [dayjs().format("MMM")];
      }
      if (!year || year.length === 0) {
        year = [String(currentYear)];
      }

      const monthsParam = Array.isArray(months) ? months.join(",") : months;

      api
        .get("/services/translations", {
          params: {
            months: monthsParam,
            year: year[0], // Single-select year, only send first value
          },
        })
        .then((response) => {
          if (response !== null) {
            const services = response.data;
            services.reverse();
            setListOfServices(services);
            setIsLoading(false);

            // Restore the scroll position once the new rows are rendered.
            // Phase 1: materialize the anchor row (single scrollToIndexes).
            // Phase 2: nudge by the measured rect delta with a settle frame
            // between every write and the next measurement — measuring on the
            // same frame as the write reads stale layout and overshoots,
            // which caused the up/down jitter. Stops as soon as the anchor is
            // aligned, when a correction stops reducing the error, or on user
            // interaction. Scoped to THIS grid's root element via its apiRef.
            const savedRowId = listFirstVisibleRowIdRef.current;
            const savedPixel = listScrollRestoreRef.current;
            if (savedRowId != null || savedPixel > 0) {
              const targetIdx =
                savedRowId != null
                  ? services.findIndex(
                      (s) => String(s.id) === String(savedRowId),
                    )
                  : -1;

              const getScroller = () => {
                const root = apiRef.current?.rootElementRef?.current;
                return (root || document).querySelector(
                  ".MuiDataGrid-virtualScroller",
                );
              };
              let cancelled = false;
              const cancelOnInteraction = () => {
                cancelled = true;
              };
              const interactionEvents = ["wheel", "mousedown", "touchstart"];
              const initialScroller = getScroller();
              interactionEvents.forEach((evt) => {
                initialScroller?.addEventListener(evt, cancelOnInteraction, {
                  once: true,
                  passive: true,
                });
              });

              const cleanup = () => {
                listScrollRestoreRef.current = 0;
                listFirstVisibleRowIdRef.current = null;
                const sc = getScroller();
                interactionEvents.forEach((evt) => {
                  sc?.removeEventListener(evt, cancelOnInteraction);
                });
              };

              let materialized = targetIdx < 0;
              let settleFrames = 0;
              let attempts = 0;
              let lastAbsDelta = null;
              const maxAttempts = 40;

              const step = () => {
                if (cancelled) {
                  cleanup();
                  return;
                }

                const sc = getScroller();
                if (!sc) {
                  attempts += 1;
                  if (attempts < maxAttempts) requestAnimationFrame(step);
                  else cleanup();
                  return;
                }

                // Phase 1 — bring the anchor row into the render window once.
                if (!materialized) {
                  apiRef.current?.scrollToIndexes?.({ rowIndex: targetIdx });
                  materialized = true;
                  settleFrames = 1;
                  requestAnimationFrame(step);
                  return;
                }

                // Let the grid apply the last write before measuring again.
                if (settleFrames > 0) {
                  settleFrames -= 1;
                  requestAnimationFrame(step);
                  return;
                }

                if (targetIdx >= 0) {
                  const rowEl = sc.querySelector(
                    `.MuiDataGrid-row[data-id="${savedRowId}"]`,
                  );
                  if (!rowEl) {
                    // Virtualizer still settling — wait (bounded).
                    attempts += 1;
                    if (attempts < maxAttempts) requestAnimationFrame(step);
                    else cleanup();
                    return;
                  }

                  const delta =
                    rowEl.getBoundingClientRect().top -
                    sc.getBoundingClientRect().top;
                  const absDelta = Math.abs(delta);
                  if (absDelta <= 2) {
                    cleanup();
                    return;
                  }
                  // If the previous nudge did not reduce the error, stop —
                  // re-asserting would just jitter between two positions.
                  if (lastAbsDelta !== null && absDelta >= lastAbsDelta - 1) {
                    cleanup();
                    return;
                  }
                  lastAbsDelta = absDelta;
                  sc.scrollTop += delta;
                  settleFrames = 1;
                } else {
                  // No usable row anchor — restore the pixel offset and nudge
                  // the virtualizer with a scroll event (a no-op scrollTop
                  // write fires no event, so the render window never updates).
                  const pxDiff = Math.abs(sc.scrollTop - savedPixel);
                  if (pxDiff <= 2) {
                    cleanup();
                    return;
                  }
                  if (lastAbsDelta !== null && pxDiff >= lastAbsDelta - 1) {
                    cleanup();
                    return;
                  }
                  lastAbsDelta = pxDiff;
                  sc.scrollTop = savedPixel;
                  sc.dispatchEvent(new Event("scroll"));
                  settleFrames = 1;
                }

                attempts += 1;
                if (attempts < maxAttempts && !cancelled) {
                  requestAnimationFrame(step);
                } else {
                  cleanup();
                }
              };
              requestAnimationFrame(step);
            }
          }
        })
        .catch((error) => {
          const message =
            error.response?.data?.message ||
            "Unable to connect to the server. Please try again later.";
          handleErrorDialog(message, theme);

          setIsLoading(false);
        });
    },
    [selectedMonths, selectedYear, currentYear, handleErrorDialog, theme],
  );

  // Listener callback for translations list refetch
  const handleTranslationsListRefetch = useCallback(
    (updatedBy) => {
      setLastUpdate(updatedBy); // Store for snackbar
      fetchTranslationsData(); // Refetch data
    },
    [fetchTranslationsData],
  );

  // Show snackbar when update is received (in separate effect to avoid listener churn)
  useEffect(() => {
    if (lastUpdate?.userName) {
      const message = lastUpdate?.projectNumber
        ? `#${lastUpdate.projectNumber} updated by ${lastUpdate.userName}`
        : `Translation updated by ${lastUpdate.userName}`;
      showSnackbar(message, theme, "info");
      setLastUpdate(null);
    }
  }, [lastUpdate, showSnackbar, theme]);

  // IDs currently visible in the filtered list — updates for records outside
  // this set are ignored (a May change doesn't reload a list filtered to August)
  const visibleServiceIds = useMemo(
    () => (listOfServices || []).map((s) => Number(s.id)),
    [listOfServices],
  );

  useAssignmentUpdates({
    modelType: "TranslationsList",
    recordId: "all",
    onUpdate: handleTranslationsListRefetch,
    visibleRecordIds: visibleServiceIds,
  });

  const getDataLists = useCallback(() => {
    api
      .all([
        api.get("/clients/brief"),
        api.get("/linguists/brief"),
        api.get("/languages"),
        api.get("/services/translations/statuses"),
        api.get("/services/translations/types"),
        api.get("/services/translations/latestProjectNumber"),
        api.get("/services/translations/rateTypes"),
        api.get("/dashboard/serviceDateRange"),
        api.get("/services/translations/deliveryTypes"),
        api.get("/services/translations/paymentTypes"),
      ])
      .then(
        api.spread(
          (
            clientsResponse,
            linguistsResponse,
            languagesResponse,
            statusesResponse,
            typesResponse,
            latestProjectNumberResponse,
            rateTypesResponse,
            minMaxDatesResponse,
            deliveryTypesResponse,
            paymentTypesResponse,
          ) => {
            if (clientsResponse) {
              setListOfClients(clientsResponse.data);
            }
            if (linguistsResponse) {
              setListOfLinguists(linguistsResponse.data);
            }
            if (languagesResponse) {
              const tempLanguages = languagesResponse.data.filter(
                (language) => language.Name !== "ALL",
              );
              setListOfLanguages(tempLanguages);
            }
            if (statusesResponse) {
              setListOfStatuses(statusesResponse.data);
            }
            if (typesResponse) {
              const tempTypes = typesResponse.data.slice(0, 5);
              setListOfTypes(tempTypes);
            }
            if (
              latestProjectNumberResponse &&
              latestProjectNumberResponse.data
            ) {
              setLatestProjectNumber(
                Number(latestProjectNumberResponse.data.ProjectNumber || 0) + 1,
              );
            }
            if (rateTypesResponse) {
              setRateTypes(rateTypesResponse.data);
            }
            if (minMaxDatesResponse) {
              setMinMaxDates(minMaxDatesResponse.data);
            }
            if (deliveryTypesResponse) {
              const filteredDeliveryTypes = deliveryTypesResponse.data.filter(
                (type) => !["CODA", "SmartCat", "In-person"].includes(type),
              );
              setDeliveryTypes(filteredDeliveryTypes);
            }
            if (paymentTypesResponse) {
              setPaymentTypes(paymentTypesResponse.data);
            }
            // Mark data lists as ready
            setIsDataListsReady(true);
          },
        ),
      )
      .catch((error) => {
        const message =
          error.response?.data?.message ||
          "Unable to connect to the server. Please try again later.";
        setErrorMessage(message);
        handleErrorDialog(message, theme);
        setIsDataListsReady(true); // Even on error, mark as ready to prevent infinite waiting
      });
  }, [handleErrorDialog, theme]);

  fetchTranslationsDataRef.current = fetchTranslationsData;
  getDataListsRef.current = getDataLists;

  useEffect(() => {
    fetchTranslationsDataRef.current();
    getDataListsRef.current();
    hasInitializedDataRef.current = true;
  }, []);

  useEffect(() => {
    if (!hasInitializedDataRef.current) {
      return;
    }

    fetchTranslationsDataRef.current();
  }, [refreshPage]);

  // Handle pending create new action when data lists are ready
  useEffect(() => {
    if (pendingCreateNew && isDataListsReady) {
      const data = {
        Clients: listOfClients,
        Linguists: listOfLinguists,
        Languages: listOfLanguages,
        Translations: listOfServices,
        TranslationStatuses: listOfStatuses,
        TranslationTypes: listOfTypes,
        ProjectNumber: latestProjectNumber,
        RateTypes: rateTypes,

        Action: OVERLAY_NAMES.TRANSLATION_ACTION_NEW,
      };

      setOverlayData(data);
      setOverlayName(OVERLAY_NAMES.TRANSLATION_NEW_EDIT);
      setPendingCreateNew(false);
    }
  }, [
    pendingCreateNew,
    isDataListsReady,
    listOfClients,
    listOfLinguists,
    listOfLanguages,
    listOfServices,
    listOfStatuses,
    listOfTypes,
    latestProjectNumber,
    rateTypes,
    setOverlayData,
    setOverlayName,
  ]);

  // Sync temporary states with actual state (if changed externally)
  useEffect(() => {
    setTempSelectedMonths(selectedMonths);
  }, [selectedMonths]);

  useEffect(() => {
    setTempSelectedYear(selectedYear);
  }, [selectedYear]);

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return dayjs(v1, "M/D/YYYY").valueOf() - dayjs(v2, "M/D/YYYY").valueOf();
  };

  const columns = [
    {
      field: "ProjectNumber",
      headerName: "Number",
      minWidth: 110,
      valueGetter: (_, params) => {
        const data = params.ProjectNumber;
        const value = data ? data : "N/A";
        return value;
      },
      flex: 1.1,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[0] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            sortModel={sortModel}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Date",
      headerName: "Date",
      valueGetter: (_, params) => {
        const data = params.Date;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 100,
      flex: 1.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            sortModel={sortModel}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Deadline",
      headerName: "Deadline",
      valueGetter: (_, params) => {
        const data = params.Deadline;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 120,
      flex: 1.2,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: getDeadlineProximityColor(
                theme,
                params.value,
                params.row?.TranslationStatus,
              ),
            }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Client",
      headerName: "Client",
      valueGetter: (_, params) => {
        const data = params.Client;
        const value = data ? data.Account : "N/A";
        return value;
      },
      minWidth: 180,
      flex: 1.8,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Requestor",
      headerName: "Requestor",
      valueGetter: (_, params) => {
        const data = params.Requestor;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={filterData[4] || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            sortModel={sortModel}
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TranslationStatus",
      headerName: "Status",
      minWidth: 120,
      flex: 1.2,
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Box
            sx={{
              borderRadius: "12px",
              px: "0.5rem",
              backgroundColor: getStatusColor(theme, params.value),
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: theme.palette.background.default,
              }}
            >
              {params.value}
            </Typography>
          </Box>
        </Box>
      ),
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AccountCharge",
      headerName: "Account Charge",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AccountCharge;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value =
            parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
        }

        return value;
      },
      minWidth: 165,
      flex: 1.65,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Details",
      headerName: "Details",
      valueGetter: (_, params) => {
        const data = params.Details;
        const value = data ? data : "N/A";
        return value;
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 450,
      flex: 4.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
        {
      field: "TranslationType",
      headerName: "Type",
      valueGetter: (_, params) => {
        const data = params.ServiceSubtype?.Name;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 100,
      flex: 1.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Linguist",
      headerName: "Linguist",
      valueGetter: (_, params) => {
        const data = params.Linguist;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TotalCost",
      headerName: "Total Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.TotalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "AdditionalCost",
      headerName: "Additional Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.AdditionalCost;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 160,
      flex: 1.6,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "LinguistPay",
      headerName: "Linguist Pay",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.LinguistPay;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 140,
      flex: 1.4,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "Profit",
      headerName: "Profit",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Profit;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 95,
      flex: 0.95,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Margin",
      headerName: "Margin",
      valueGetter: (_, params) => {
        const data = params.Margin;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          const percentage = data * 1;
          if (percentage === 0) {
            value = "0%";
          } else if (percentage === 100) {
            value = "100%";
          } else {
            value = `${percentage.toFixed(2)}%`;
          }
        }

        return value;
      },
      minWidth: 100,
      flex: 1.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Language",
      headerName: "Source Language",
      valueGetter: (_, params) => {
        const data = params.Language;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 170,
      flex: 1.7,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "TargetLanguage",
      headerName: "Target Languages",
      valueGetter: (_, params) => {
        const languages = params.ServiceTargetLanguages;
        if (!languages || languages.length === 0) return "N/A";
        // Extract the Language.Name from each item and filter out any empty values
        return languages
          .map((item) =>
            item.Language && item.Language.Name ? item.Language.Name : "",
          )
          .filter((name) => name)
          .join(" | ");
      },
      renderCell: (params) => (
        <Box style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <Typography
            sx={{
              display: "-webkit-box",
              overflow: "hidden",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
            }}
            style={{ whiteSpace: "pre-wrap" }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
      minWidth: 300,
      flex: 3.0,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "UnitCount",
      headerName: "Units",
      valueGetter: (_, params) => {
        const count = params.UnitCount;

        if (count === null || count === undefined || count === "") {
          return "N/A";
        }

        const unitName = params.UnitType?.Name || "unit";
        const unitLabel =
          Number(count) === 1
            ? unitName.toLowerCase()
            : `${unitName.toLowerCase()}s`;

        return `${Number(count).toLocaleString()} ${unitLabel}`;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "NumberOfLanguages",
      headerName: "# Of Languages",
      valueGetter: (_, params) => {
        const data = params.NumberOfLanguages;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 160,
      flex: 1.6,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Rate",
      headerName: "Rate",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.Rate;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
        }

        return value;
      },
      minWidth: 90,
      flex: 0.9,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "RateType",
      headerName: "Rate Type",
      valueGetter: (_, params) => {
        const data = params.RateType;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "NotarizationOrCOTCost",
      headerName: "Not/COT Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.NotarizationOrCOTCost;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "DeliveryType",
      headerName: "Delivery Type",
      valueGetter: (_, params) => {
        const data = params.DeliveryType;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 155,
      flex: 1.55,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "DeliveryCost",
      headerName: "Delivery Cost",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.DeliveryCost;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 150,
      flex: 1.5,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "PaymentType",
      headerName: "Payment Type",
      valueGetter: (_, params) => {
        const data = params.PaymentType;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 155,
      flex: 1.55,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "PaymentFees",
      headerName: "Payment Fees",
      sortComparator: numericComparator,
      valueGetter: (_, params) => {
        const data = params.PaymentFees;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 155,
      flex: 1.55,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "DeliveryDate",
      headerName: "Delivery Date",
      valueGetter: (_, params) => {
        const data = params.DeliveryDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      sortComparator: dateComparator,
      minWidth: 155,
      flex: 1.55,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Paid",
      headerName: "Paid",
      valueGetter: (_, params) => {
        const data = params.Paid;
        const value = data ? data : "N/A";
        return value;
      },
      minWidth: 85,
      flex: 0.85,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },

    {
      field: "Cancelled",
      headerName: "Cancelled",
      valueGetter: (_, params) => {
        const data = params.Cancelled;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Cancelled;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CancellationReason",
      headerName: "Cancellation Reason",
      valueGetter: (_, params) => {
        const data = params.CancellationReason;
        const value = data && data !== "" ? data : "N/A";
        return value;
      },
      minWidth: 195,
      flex: 1.95,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "InvoiceTo",
      headerName: "Invoice To",
      valueGetter: (_, params) => {
        const data = params.Invoicer;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 125,
      flex: 1.25,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "Invoiced",
      headerName: "Invoiced",
      valueGetter: (_, params) => {
        const data = params.Invoiced;
        let value = "N/A";

        if (data !== undefined && data !== null) {
          value = data ? "Yes" : "No";
        }

        return value;
      },
      renderCell: (params) => {
        const data = params.row.Invoiced;
        if (data === undefined || data === null) return "N/A";
        return (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", height: "21px" }}>
              {data ? (
                <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
              ) : (
                <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
              )}
            </Box>
          </Box>
        );
      },
      minWidth: 120,
      flex: 1.2,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CreatedBy",
      headerName: "Created By",
      valueGetter: (_, params) => {
        const data = params.Creator;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 130,
      flex: 1.3,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CheckedBy",
      headerName: "Checked By",
      valueGetter: (_, params) => {
        const data = params.Checker;
        const value = data ? data.Name : "N/A";
        return value;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
    {
      field: "CheckedOn",
      headerName: "Checked On",
      valueGetter: (_, params) => {
        const data = params.CheckingDate;
        const value = data ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
        return value;
      },
      minWidth: 135,
      flex: 1.35,
      renderHeader: (params) => (
        <DataGridHeader headerName={params.colDef.headerName}>
          <ColumnDateFilter
            data={listOfServices || []}
            field={params.field}
            valueGetter={params.colDef.valueGetter}
            selectedValues={columnFilters[params.field] || []}
            onFilterChange={(values) =>
              handleColumnFilterChangeWithStorage(params.field, values)
            }
            onSortChange={(field, direction) => {
              const newSortModel = direction
                ? [{ field, sort: direction }]
                : [];
              setSortModel(newSortModel);
              handleSortModelChange(newSortModel);
            }}
            currentSort={
              sortModel.find((s) => s.field === params.field)?.sort || null
            }
            openFilter={openFilter}
            setOpenFilter={setOpenFilter}
          />
        </DataGridHeader>
      ),
    },
  ];

  // Filter columns based on columnsVisible state
  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(CONST.TRANSLATION_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const getStoredSortModel = () => {
    const stored = localStorage.getItem(CONST.TRANSLATION_SORT_KEY);
    return stored ? JSON.parse(stored) : [];
  };

  const [filterModel, setFilterModel] = useState(getStoredFilterModel);
  const [filterRowIds, setFilterRowIds] = useState([]);
  const prevFilteredRowIdsRef = useRef([]);
  const [sortModel, setSortModel] = useState(getStoredSortModel);

  // Global filter state management
  const [openFilter, setOpenFilter] = useState(null);

  // Column filters
  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listOfServices || [], columns);

  const handleColumnFilterChangeRef = useRef(handleColumnFilterChange);
  const hasAppliedSavedFiltersRef = useRef(false);

  useEffect(() => {
    handleColumnFilterChangeRef.current = handleColumnFilterChange;
  }, [handleColumnFilterChange]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback(
    (field, values) => {
      handleColumnFilterChange(field, values);
      saveColumnFilter(field, values);
    },
    [handleColumnFilterChange, saveColumnFilter],
  );

  // Load saved column filters on mount and apply them
  useEffect(() => {
    if (!listOfServices || listOfServices.length === 0) {
      hasAppliedSavedFiltersRef.current = false;
      return;
    }

    if (hasAppliedSavedFiltersRef.current) {
      return;
    }

    const savedFilters = loadAllColumnFilters();
    Object.entries(savedFilters).forEach(([field, values]) => {
      handleColumnFilterChangeRef.current(field, values);
    });

    hasAppliedSavedFiltersRef.current = true;
  }, [listOfServices, loadAllColumnFilters]); // Apply saved filters once per loaded dataset

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();

    // Clear quick filter and persist the empty model for this grid
    const clearedModel = { items: [], quickFilterValues: [] };
    setFilterModel(clearedModel);
    localStorage.setItem(
      CONST.TRANSLATION_FILTER_KEY,
      JSON.stringify(clearedModel),
    );
  }, [clearAllFilters, clearAllColumnFilters]);

  // Check if quick filter has values
  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (row) => row.ProjectNumber || "N/A",
      },
      {
        field: "Date",
        valueGetter: (row) =>
          row.Date ? dayjs.tz(row.Date).format("M/D/YYYY") : "N/A",
      },
      {
        field: "Deadline",
        valueGetter: (row) =>
          row.Deadline ? dayjs.tz(row.Deadline).format("M/D/YYYY") : "N/A",
      },
      {
        field: "Client",
        valueGetter: (row) => (row.Client ? row.Client?.Account : "N/A"),
      },
      {
        field: "Requestor",
        valueGetter: (row) => (row.Requestor ? row.Requestor.Name : "N/A"),
      },
      {
        field: "TranslationStatus",
        valueGetter: (row) => row.TranslationStatus || "N/A",
      },
      {
        field: "AccountCharge",
        valueGetter: (row) => {
          const data = row.AccountCharge;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0
              ? "N/A"
              : `$${parseFloat(data).toLocaleString()}`;
          }
          return "N/A";
        },
      },
      { field: "Details", valueGetter: (row) => row.Details || "N/A" },
      {
        field: "Linguist",
        valueGetter: (row) => (row.Linguist ? row.Linguist.Name : "N/A"),
      },
      {
        field: "TotalCost",
        valueGetter: (row) => {
          const data = row.TotalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "AdditionalCost",
        valueGetter: (row) => {
          const data = row.AdditionalCost;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "LinguistPay",
        valueGetter: (row) => {
          const data = row.LinguistPay;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Profit",
        valueGetter: (row) => {
          const data = row.Profit;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "Margin",
        valueGetter: (row) => {
          const data = row.Margin;
          if (data !== undefined && data !== null) {
            const percentage = data * 1;
            if (percentage === 0) return "0%";
            if (percentage === 100) return "100%";
            return `${percentage.toFixed(2)}%`;
          }
          return "N/A";
        },
      },
      {
        field: "Language",
        valueGetter: (row) => (row.Language ? row.Language.Name : "N/A"),
      },
      {
        field: "TargetLanguage",
        valueGetter: (row) => {
          const languages = row.ServiceTargetLanguages;
          if (!languages || languages.length === 0) return "N/A";
          return languages
            .map((item) =>
              item.Language && item.Language.Name ? item.Language.Name : "",
            )
            .filter((name) => name)
            .join(" | ");
        },
      },
      {
        field: "UnitCount",
        valueGetter: (row) => {
          const count = row.UnitCount;

          if (count === null || count === undefined || count === "") {
            return "N/A";
          }

          const unitName = row.UnitType?.Name || "unit";
          const unitLabel =
            Number(count) === 1
              ? unitName.toLowerCase()
              : `${unitName.toLowerCase()}s`;

          return `${Number(count).toLocaleString()} ${unitLabel}`;
        },
      },
      {
        field: "NumberOfLanguages",
        valueGetter: (row) => row.NumberOfLanguages || "N/A",
      },
      {
        field: "Rate",
        valueGetter: (row) => {
          const data = row.Rate;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      { field: "RateType", valueGetter: (row) => row.RateType || "N/A" },
      {
        field: "NotarizationOrCOTCost",
        valueGetter: (row) => row.NotarizationOrCOTCost || "N/A",
      },
      {
        field: "DeliveryType",
        valueGetter: (row) => row.DeliveryType || "N/A",
      },
      {
        field: "DeliveryCost",
        valueGetter: (row) => row.DeliveryCost || "N/A",
      },
      { field: "PaymentType", valueGetter: (row) => row.PaymentType || "N/A" },
      { field: "PaymentFees", valueGetter: (row) => row.PaymentFees || "N/A" },
      {
        field: "DeliveryDate",
        valueGetter: (row) =>
          row.DeliveryDate
            ? dayjs.tz(row.DeliveryDate).format("M/D/YYYY")
            : "N/A",
      },
      { field: "Paid", valueGetter: (row) => row.Paid || "N/A" },
      {
        field: "TranslationType",
        valueGetter: (row) => row.ServiceSubtype?.Name || "N/A",
      },
      {
        field: "Cancelled",
        valueGetter: (row) => {
          const data = row.Cancelled;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "CancellationReason",
        valueGetter: (row) =>
          row.CancellationReason && row.CancellationReason !== ""
            ? row.CancellationReason
            : "N/A",
      },
      {
        field: "InvoiceTo",
        valueGetter: (row) => (row.Invoicer ? row.Invoicer.Name : "N/A"),
      },
      {
        field: "Invoiced",
        valueGetter: (row) => {
          const data = row.Invoiced;
          if (data !== undefined && data !== null) {
            return data ? "Yes" : "No";
          }
          return "N/A";
        },
      },
      {
        field: "CreatedBy",
        valueGetter: (row) => (row.Creator ? row.Creator.Name : "N/A"),
      },
      {
        field: "CheckedBy",
        valueGetter: (row) => (row.Checker ? row.Checker.Name : "N/A"),
      },
      {
        field: "CheckedOn",
        valueGetter: (row) =>
          row.CheckingDate
            ? dayjs.tz(row.CheckingDate).format("M/D/YYYY")
            : "N/A",
      },
    ],
    [],
  );

  // Cross-filtering data calculation
  const filterData = useMemo(() => {
    return filterColumns.map((column, index) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      let filteredData;
      if (otherFilters.length === 0) {
        filteredData = listOfServices || [];
      } else {
        filteredData = (listOfServices || []).filter((row) => {
          return otherFilters.every(([field, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;

            const filterColumn = filterColumns.find(
              (col) => col.field === field,
            );
            if (!filterColumn) return true;

            const value = filterColumn.valueGetter(row);
            return selectedValues.includes(value);
          });
        });
      }

      // Limit Requestor data to top 50 most frequent requestors
      if (column.field === "Requestor" && filteredData.length > 0) {
        const requestorCounts = {};
        filteredData.forEach((row) => {
          const requestor = column.valueGetter(row);
          requestorCounts[requestor] = (requestorCounts[requestor] || 0) + 1;
        });

        const topRequestors = Object.entries(requestorCounts)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 50)
          .map(([requestor]) => requestor);

        return filteredData.filter((row) => {
          const requestor = column.valueGetter(row);
          return topRequestors.includes(requestor);
        });
      }

      return filteredData;
    });
  }, [columnFilters, listOfServices, filterColumns]);

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(
      CONST.TRANSLATION_FILTER_KEY,
      JSON.stringify(newFilterModel),
    );
  };

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    localStorage.setItem(
      CONST.TRANSLATION_SORT_KEY,
      JSON.stringify(newSortModel),
    );
  };

  let totalAccountCharge = 0;
  let totalTotalCost = 0;
  let totalProfit = 0;

  const filteredRowIdsSet = new Set((filterRowIds || []).map(String));
  const includeAll = filteredRowIdsSet.size === 0;

  // Use columnFilteredData for totals calculation to include column filter effects
  const dataForTotals = columnFilteredData || [];

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    totalAccountCharge += Number(service.AccountCharge);
    totalTotalCost += Number(service.TotalCost);
    totalProfit += Number(service.Profit);
  });

  const listOfTotals = [
    { name: "Total Account Charge", total: totalAccountCharge },
    { name: "Total Total Cost", total: totalTotalCost },
    { name: "Total Profit", total: totalProfit },
  ];

  // Count statuses for Members
  let completedCount = 0;
  let deliveredCount = 0;
  let inProgressCount = 0;

  dataForTotals.forEach((service) => {
    const include = includeAll || filteredRowIdsSet.has(String(service.id));
    if (!include) return;

    const status = service.TranslationStatus;
    if (status === "Completed") completedCount++;
    else if (status === "Delivered") deliveredCount++;
    else if (status === "In-Progress") inProgressCount++;
  });

  const listOfServiceStatuses = [
    { name: "Completed", count: completedCount },
    { name: "Delivered", count: deliveredCount },
    { name: "In-Progress", count: inProgressCount },
  ];

  // State for toggling between totals and statuses
  // Check localStorage first, then fall back to role-based default (Admin: totals, Member: statuses)
  const getInitialShowTotals = () => {
    const saved = localStorage.getItem(CONST.TRANSLATIONS_SHOW_TOTALS_KEY);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return currentUser?.role !== "Admin";
  };

  const [showTotals, setShowTotals] = useState(getInitialShowTotals);

  // Save to localStorage when toggling
  const handleToggleShowTotals = () => {
    const newValue = !showTotals;
    setShowTotals(newValue);
    localStorage.setItem(
      CONST.TRANSLATIONS_SHOW_TOTALS_KEY,
      JSON.stringify(newValue),
    );
  };

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters: toolbarHasFilters,
      onClear,
      theme: toolbarTheme,
      selectionEnabled: toolbarSelectionEnabled,
      selectionModel: toolbarSelectionModel,
      allSelectedChecked: toolbarAllSelectedChecked,
      onCancelSelection,
      onMarkChecked,
    }) => {
      const isMobileToolbar = isMobile;
      const [anchorEl, setAnchorEl] = useState(null);
      const open = Boolean(anchorEl);

      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      const buttons = [];

      if (toolbarSelectionEnabled) {
        buttons.push({
          label: "Cancel Selection",
          onClick: () => {
            onCancelSelection();
            handleClose();
          },
          icon: null,
        });
        buttons.push({
          label: "Mark As Checked",
          onClick: () => {
            onMarkChecked();
            handleClose();
          },
          icon: <TaskAltRoundedIcon />,
          disabled: toolbarSelectionModel.length === 0,
          isChecked: toolbarAllSelectedChecked,
        });
      }

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {!isMobileToolbar && toolbarSelectionEnabled && (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={onCancelSelection}
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                Cancel Selection
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<TaskAltRoundedIcon />}
                disabled={toolbarSelectionModel.length === 0}
                onClick={onMarkChecked}
                sx={{
                  mr: 1,
                  color: toolbarAllSelectedChecked
                    ? toolbarTheme.palette.text.disabled
                    : toolbarTheme.palette.primary.main,
                  borderColor: toolbarAllSelectedChecked
                    ? toolbarTheme.palette.action.disabled
                    : toolbarTheme.palette.primary.main,
                  "&:hover": toolbarAllSelectedChecked
                    ? {
                        borderColor: toolbarTheme.palette.action.disabled,
                        backgroundColor: "transparent",
                        color: toolbarTheme.palette.text.disabled,
                      }
                    : {
                        borderColor:
                          toolbarTheme.palette.primary.buttonHoverSelected,
                        backgroundColor:
                          toolbarTheme.palette.primary.buttonHoverSelected,
                        color: toolbarTheme.palette.background.default,
                      },
                }}
              >
                Mark As Checked
              </Button>
            </>
          )}

          {isMobileToolbar && buttons.length > 0 && (
            <>
              <Tooltip title="Actions">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    mx: 1,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  <MoreHorizRoundedIcon />
                </IconButton>
              </Tooltip>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 200 }}>
                  {buttons.map((btn, index) => (
                    <MenuItem
                      key={index}
                      onClick={btn.onClick}
                      disabled={btn.disabled}
                      sx={{
                        color: btn.isChecked
                          ? theme.palette.text.disabled
                          : theme.palette.primary.main,
                      }}
                    >
                      {btn.icon && (
                        <ListItemIcon
                          sx={{
                            color: btn.isChecked
                              ? theme.palette.text.disabled
                              : theme.palette.primary.main,
                          }}
                        >
                          {btn.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText>{btn.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Box>
              </Popover>
            </>
          )}

          <Box sx={{ flex: 1 }} />

          {!isMobileToolbar && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {toolbarHasFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobileToolbar && toolbarHasFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  mx: 1,
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "top",
              gap: 1,
            }}
          >
            <CEQuickFilter iconOnly={isMobileToolbar} />

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: toolbarTheme.palette.grey.main,
                  borderColor: toolbarTheme.palette.divider,
                  border: `1px solid ${toolbarTheme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: toolbarTheme.palette.primary.main,
                    color: toolbarTheme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openDialog({
                    title: "Export Data",
                    theme: theme,
                    customContent: <ExportDialogContent />,
                  });
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [theme, isMobile, openDialog]);

  const customFooter = () => {
    return (
      <GridFooterContainer>
        {/* Add what you want here */}

        <Box
          sx={{
            p: "1rem",
            display: "flex",
            flexWrap: "wrap", // wrap to new line if too long
            gap: 2, // spacing between items
            alignItems: "center",
          }}
        >
          <IconButton
            onClick={handleToggleShowTotals}
            size="small"
            sx={{
              color: theme.palette.primary.main,
              "&:hover": {
                color: theme.palette.primary.buttonHoverSelected,
              },
            }}
          >
            <SwapHorizRoundedIcon fontSize="small" />
          </IconButton>

          {showTotals
            ? listOfTotals.map((total) => {
                return (
                  <Box key={`${total}-${total.name}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexWrap: "wrap", justifyContent: "center" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {total.name}:
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: theme?.palette?.secondary?.[400],
                          fontWeight: 600,
                        }}
                      >
                        ${Number(total.total).toLocaleString()}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })
            : listOfServiceStatuses.map((status) => {
                return (
                  <Box key={`${status}-${status.name}`}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ flexWrap: "wrap", justifyContent: "center" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {status.name}:
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: theme?.palette?.secondary?.[400],
                          fontWeight: 600,
                        }}
                      >
                        {status.count}
                      </Typography>
                    </Stack>
                  </Box>
                );
              })}
        </Box>

        <GridFooter
          sx={{
            border: "none", // remove double border
          }}
        />
      </GridFooterContainer>
    );
  };

  // Overlay for Density & Columns
  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {overlayMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOverlayOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {overlayMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={
                !columns.some((col) => columnsVisible[col.field] === false)
              }
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {columns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

    </Box>
  );

  // Export Dialog Content Component
  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No translations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services

              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              const csvRows = dataToExport
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.field) {
                            // Check if this field has a special mapping
                            const mappedPath = fieldPaths[col.field];
                            if (mappedPath) {
                              value = getFieldValue(row, mappedPath);
                            } else if (col.field === "TargetLanguage") {
                              // Special handling for TargetLanguage (complex nested array)
                              const languages = row.ServiceTargetLanguages;
                              if (languages && Array.isArray(languages)) {
                                value = languages
                                  .map((tl) => tl.Language?.Name || "")
                                  .join(" | ");
                              }
                            } else {
                              value = getFieldValue(row, col.field);
                              if (value === "" && col.field in row) {
                                value = row[col.field];
                              }
                            }
                          }
                          const strValue = String(value || "").replace(
                            /"/g,
                            '""',
                          );
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(
                      `Error processing row ${rowIndex}:`,
                      rowError.message,
                    );
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `translations-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No translations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services
              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              // Create workbook and worksheet
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Translations");

              // Add header row with formatting
              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              // Track column widths
              const columnWidths = new Array(visibleColumns.length).fill(15);

              // Add data rows and calculate widths
              dataToExport.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.field) {
                      const mappedPath = fieldPaths[col.field];
                      if (mappedPath) {
                        value = getFieldValue(row, mappedPath);
                      } else if (col.field === "TargetLanguage") {
                        const languages = row.ServiceTargetLanguages;
                        if (languages && Array.isArray(languages)) {
                          value = languages
                            .map((tl) => tl.Language?.Name || "")
                            .join(" | ");
                        }
                      } else {
                        value = getFieldValue(row, col.field);
                        if (value === "" && col.field in row) {
                          value = row[col.field];
                        }
                      }
                    }
                    const strValue = String(value || "");
                    // Update column width based on content
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              // Apply calculated column widths
              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });

              // Generate and download file
              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `translations-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  return (
    <Box
      //borderRadius="1.5rem 1.5rem 0 0"
      p="1rem 1rem 0 1rem"
      //m=".5rem 1.5rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`, // Take full viewport height minus margin
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header section */}
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid
            size={{ xs: 12 }}
            container
            justifyContent="space-between"
            sx={{ px: 1 }}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={2}
            >
              <Box
                sx={{
                  flex: isMobile ? 0 : 1,
                  maxWidth: isMobile ? undefined : FILTER_SELECTOR_WIDTH,
                }}
              >
                <CustomDateSelector
                  label="Date Range"
                  months={months}
                  years={
                    yearOptions.length > 0 ? yearOptions : [String(currentYear)]
                  }
                  selectedMonths={tempSelectedMonths}
                  selectedYears={tempSelectedYear}
                  onSave={handleDateSelectionSave}
                  isMultiMonth={true}
                  isMultiYear={false}
                  monthGridItemSize={3}
                  yearGridItemSize={4}
                  showClearFilter={false}
                  iconOnly={isMobile}
                />
              </Box>
              <Box display="flex" gap={2} alignItems="center">
                {!isMobile ? (
                  <Button
                    onClick={() => {
                      setOverlayName(OVERLAY_NAMES.TRANSLATION_DOCUMENTS);
                    }}
                    startIcon={<DescriptionRoundedIcon />}
                    sx={{
                      width: "200px",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      padding: "8px 16px",
                      backgroundColor: theme.palette.background.default,
                      borderRadius: "12px",
                      boxShadow:
                        "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                      color: theme.palette.primary.main,
                      textTransform: "none",
                      fontSize: "0.875rem",
                      "& .MuiButton-startIcon": {
                        marginRight: 0,
                      },
                      "& .MuiButton-endIcon": {
                        display: "none",
                      },
                      "&:hover": {
                        backgroundColor: theme.palette.primary.buttonHover,
                        boxShadow:
                          "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    <Box sx={{ flex: 1, textAlign: "center" }}>Documents</Box>
                  </Button>
                ) : (
                  <Tooltip title="Documents">
                    <IconButton
                      onClick={() => {
                        setOverlayName(OVERLAY_NAMES.TRANSLATION_DOCUMENTS);
                      }}
                      sx={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: theme.palette.background.default,
                        boxShadow:
                          "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                        borderRadius: "12px",
                        border: "none",
                        color: theme.palette.primary.main,
                        "&:hover": {
                          backgroundColor: theme.palette.primary.buttonHover,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                        },
                      }}
                    >
                      <DescriptionRoundedIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Button
                  onClick={() => {
                    setPendingCreateNew(true);
                  }}
                  startIcon={!isMobile && <AddRoundedIcon />}
                  sx={{
                    width: isMobile ? "40px" : "200px",
                    minWidth: isMobile ? "40px" : "200px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: isMobile ? 0 : "8px 16px",
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "12px",
                    boxShadow:
                      "0 2px 8px rgba(0, 0, 0, 0.1), 0px 4px 8px rgba(0, 0, 0, 0.1)",
                    color: theme.palette.background.default,
                    textTransform: "none",
                    fontSize: "0.875rem",
                    "& .MuiButton-startIcon": {
                      margin: isMobile ? 0 : undefined,
                    },
                    "& .MuiButton-endIcon": {
                      display: "none",
                    },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                      boxShadow:
                        "0 4px 16px rgba(0, 0, 0, 0.15), 0px 8px 16px rgba(0, 0, 0, 0.15)",
                    },
                  }}
                >
                  {isMobile ? <AddRoundedIcon /> : <Box sx={{ flex: 1, textAlign: "center" }}>Add Translation</Box>}
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* DataGrid section */}
      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          boxShadow: theme.shadows[3],
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        <DataGrid
          localeText={{
            noRowsLabel: "No translation projects added yet for this month.",
            noResultsOverlayLabel: "No matches for your filters",
          }}
          apiRef={apiRef}
          density={density}
          loading={isLoading}
          pageSizeOptions={[20, 50, 100]}
          getRowId={(row) => row.id}
          rows={columnFilteredData || []}
          checkboxSelection={selectionEnabled}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={handleRowSelectionModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          onStateChange={(e) => {
            const lookup = e.filter?.filteredRowsLookup;
            if (!lookup) return;

            const nextIds = Object.keys(lookup).filter((key) => lookup[key]);
            nextIds.sort();

            const prevIds = prevFilteredRowIdsRef.current;
            const isSame =
              prevIds.length === nextIds.length &&
              prevIds.every((id, idx) => id === nextIds[idx]);

            if (!isSame) {
              prevFilteredRowIdsRef.current = nextIds;
              setFilterRowIds(nextIds);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columns={filteredColumns}
          disableColumnMenu
          disableColumnSorting
          //rowSelectionModel={openFilter ? [] : undefined}
          disableRowSelectionOnClick={!!openFilter}
          onRowClick={(params, event) => {
            if (openFilter) {
              setOpenFilter(null);
              return;
            }

            if (selectionEnabled) {
              if (suppressNextClickRef.current) {
                event.defaultMuiPrevented = true;
                suppressNextClickRef.current = false;
              }
              return;
            }

            setSelectionModel([params.id]);
          }}
          onRowDoubleClick={(params) => {
            if (openFilter || selectionEnabled) return;
            const data = {
              Translation: params.row,
              TranslationID: params.row.id,

              Clients: listOfClients,
              Linguists: listOfLinguists,
              Languages: listOfLanguages,
              Translations: listOfServices,
              TranslationStatuses: listOfStatuses,
              TranslationTypes: listOfTypes,
              ProjectNumber: latestProjectNumber,
              RateTypes: rateTypes,

              ComingFrom: `/translations`,
            };

            navigate(`/translations/${params.row.id}`, { state: data });
          }}
          getCellClassName={(params) => {
            return params.value === "No" ||
              params.value === "N/A" ||
              params.value === "0%" ||
              params.value === "null"
              ? "notAvailable"
              : "";
          }}
          slots={{ toolbar: CustomToolbar, footer: customFooter }}
          slotProps={{
            row: {
              onContextMenu: handleContextMenu,
              style: { cursor: "context-menu" },
              onMouseDown: handleRowMouseDown,
              onMouseUp: handleRowMouseUp,
              onMouseLeave: handleRowMouseUp,
              onTouchStart: handleRowTouchStart,
              onTouchEnd: handleRowTouchEnd,
              onTouchCancel: handleRowTouchEnd,
            },
            loadingOverlay: {
              variant: "skeleton",
              noRowsVariant: "skeleton",
            },
            toolbar: {
              hasAnyActiveFilters,
              onClear: () => clearAllFiltersRef.current(),
              theme,
              selectionEnabled,
              selectionModel,
              allSelectedChecked: selectionStats.allChecked,
              onCancelSelection: handleCancelSelection,
              onMarkChecked: handleMarkSelectedAsChecked,
              showQuickFilter: true,
              printOptions: { disableToolbarButton: true },
              csvOptions: { disableToolbarButton: true },
            },
          }}
          ignoreDiacritics
          sx={{
            backgroundColor: theme.palette.background.default,
            borderRadius: "12px",
            height: "100%", // Make DataGrid take full height of container
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: "bold",
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-scrollbarFiller": {
              backgroundColor: theme.palette.background.alt,
            },
            "& .MuiDataGrid-sortIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-menuIconButton": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
              display: "none !important",
            },
            "& .MuiDataGrid-columnHeaderTitleContainerContent": {
              flex: 1,
            },
            "& .MuiDataGrid-columnSeparator": {
              color: theme.palette.primary.main,
            },
            "& .MuiButton-root": {
              color: theme.palette.primary.main,
            },
            "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
              {
                outline: "none !important",
              },
          }}
        />
      </Box>

      {/* Context menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            sx: {
              bgcolor: theme.palette.background.alt,
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiListItemIcon-root": {
                color: theme.palette.primary.main,
              },
            },
          },
        }}
      >
        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuEdit}
        >
          <ListItemIcon>
            <EditRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Edit
          </ListItemText>
        </MenuItem>

        {canCompleteSelected && (
          <MenuItem
            onMouseEnter={handleStatusMenuClose}
            onClick={handleContextMenuComplete}
          >
            <ListItemIcon>
              <TaskRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText sx={{ color: theme.palette.primary.text }}>
              Complete
            </ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onMouseEnter={handleStatusMenuOpen}
          onMouseLeave={handleStatusMenuLeave}
          onClick={(event) => event.stopPropagation()}
        >
          <ListItemIcon>
            <SwapHorizRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Status
          </ListItemText>
          <ChevronRightRoundedIcon fontSize="small" />
        </MenuItem>

        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuDuplicate}
        >
          <ListItemIcon>
            <ContentCopyRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Duplicate
          </ListItemText>
        </MenuItem>

        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuAddTask}
        >
          <ListItemIcon>
            <AddTaskRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Add Task
          </ListItemText>
        </MenuItem>

        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuAddNote}
        >
          <ListItemIcon>
            <CommentRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Add Note
          </ListItemText>
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuCallRequestor}
        >
          <ListItemIcon>
            <CallRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Call{" "}
            {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
              ?.Name || "Requestor"}
          </ListItemText>
        </MenuItem>

        <MenuItem
          onMouseEnter={handleStatusMenuClose}
          onClick={handleContextMenuEmailRequestor}
        >
          <ListItemIcon>
            <EmailRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.primary.text }}>
            Email{" "}
            {listOfServices?.find(({ id }) => id === selectedRow)?.Requestor
              ?.Name || "Requestor"}
          </ListItemText>
        </MenuItem>
      </Menu>

      <Popper
        open={Boolean(statusMenuAnchor)}
        anchorEl={statusMenuAnchor}
        placement="right-start"
        sx={{ zIndex: theme.zIndex.modal + 1 }}
      >
        <Paper
          elevation={8}
          onMouseEnter={handleStatusMenuEnter}
          onMouseLeave={handleStatusMenuLeave}
          sx={{
            minWidth: 150,
            py: 1,
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "12px",
            "& .MuiListItemIcon-root": {
              color: theme.palette.primary.main,
            },
          }}
        >
          {statusOptions.map((status) => (
            <MenuItem
              key={status}
              onClick={() => handleContextMenuStatusChange(status)}
            >
              <ListItemIcon>
                {status === currentStatus ? (
                  <CheckRoundedIcon fontSize="small" />
                ) : (
                  <span style={{ width: 20 }} />
                )}
              </ListItemIcon>
              <ListItemText sx={{ color: theme.palette.primary.text }}>
                {status}
              </ListItemText>
            </MenuItem>
          ))}
        </Paper>
      </Popper>

      {overlayOpen && overlayMode && (
        <Popover
          open={overlayOpen}
          anchorReference="anchorPosition"
          anchorPosition={{
            left: window.innerWidth / 2,
            top: window.innerHeight / 2,
          }}
          onClose={() => setOverlayOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              borderRadius: "12px",
              maxWidth: "calc(100vw - 2rem)",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.5)"
                  : "0 8px 32px rgba(0,0,0,0.15)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}
    </Box>
  );
};

export default Translations;
